import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { isElectron } from '~/utils/common'

/**
 * Auto-fire a single update check when the Updates settings pane is opened via
 * the release email's "Update Desktop App" deep link
 * (`mango://settings/updates?check`), so the user lands on a check already in
 * progress instead of having to click "Check for updates" themselves.
 *
 * No-op outside Electron (the flag only arrives via the desktop deep link) or
 * when `?check` is absent, and fires at most once per mount — subsequent
 * re-renders (e.g. the updater's status message updating) must not re-trigger.
 */
export const useAutoUpdateCheck = (): void => {
  const [searchParams] = useSearchParams()
  const { checkForUpdates } = useElectronUpdaterContext()
  const hasCheckedRef = useRef(false)
  const wantsCheck = searchParams.has('check')

  useEffect(() => {
    if (!isElectron || !wantsCheck || hasCheckedRef.current) return
    hasCheckedRef.current = true
    checkForUpdates()
  }, [wantsCheck, checkForUpdates])
}
